import {
  Injectable,
  Renderer2,
  RendererFactory2,
  ViewEncapsulation
} from '@angular/core';
import {
  fontsApiKey,
  fontsApiUrl,
  buildGoogleFontLink
} from '@element451-libs/config451';
import { FontsApi } from '@element451-libs/models451';
import { DocumentRef } from '@element451-libs/utils451/document';
import { from, Observable } from 'rxjs';

export type Font = Pick<FontsApi.Font, 'family' | 'category' | 'variants'>;

@Injectable({
  providedIn: 'root'
})
export class FontService {
  renderer: Renderer2;

  constructor(
    rendererFactory: RendererFactory2,
    private documentRef: DocumentRef
  ) {
    this.renderer = rendererFactory.createRenderer(documentRef.head, {
      id: 'font-service',
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {}
    });
  }

  loadFonts(fonts: Font[]) {
    if (fonts) {
      fonts.forEach(font => this.loadFontFamily(font.family, font.variants));
    }
  }

  removeFonts(fonts: Font[]) {
    fonts.forEach(font => this.removeLink(font.family, font.variants));
  }

  getFonts(
    sort: FontsApi.FontSort = FontsApi.FontSort.Alpha
  ): Observable<FontsApi.FontsResponse> {
    const apiUrl = `${fontsApiUrl}?key=${fontsApiKey}&sort=${sort}`;
    const response = fetch(apiUrl).then(res => res.json());
    return from(response);
  }

  private loadFontFamily(family: string, variants?: string[]) {
    const link = buildGoogleFontLink(family, variants);
    const query = this.documentRef.head.querySelectorAll(
      `link[href='${link}']`
    );
    // Add link only if it already doesn't exist
    if (!query.length) {
      this.addLink(link);
    }
  }

  private addLink(href: string) {
    const link: HTMLLinkElement = this.renderer.createElement('link');

    link.rel = 'stylesheet';
    link.href = href;

    this.renderer.appendChild(this.documentRef.head, link);
  }

  private removeLink(family: string, variants?: string[]) {
    const link = buildGoogleFontLink(family, variants);
    const element = this.documentRef.head.querySelector(`link[href='${link}']`);
    if (element) {
      this.renderer.removeChild(this.documentRef.head, element);
    }
  }
}
